import {
  Minion,
  MinionStats,
  MinionType,
  QueuedMinion,
  SimulationState,
  Tower,
  TowerTier,
  JungleCamp,
  Team,
  Lane,
  CELL_SIZE,
  ACQUISITION_RANGE_GRID,
  FIRST_WAVE_TIME,
  MINION_SPAWN_INTERVAL,
} from './simulationTypes';
import { LANE_PATHS } from './lanePaths';
import { createTowers, TIER_ORDER } from './towerData';
import { createJungleCamps, BARON_PIT_SEQUENCE } from './jungleCampData';

/* ══════════════════ AI / spread constants ══════════════════ */

/** Perpendicular offset range per minion type (±grid cells) */
const LANE_OFFSET_RANGE: Record<MinionType, number> = {
  melee: 4, // ≈ ±125 game units
  caster: 3, // ≈ ±94 game units
  siege: 1.5,
  super: 2,
};

/** Minions within this distance push apart (grid cells) */
const SEPARATION_RADIUS = 2.5;
/** Push strength (grid cells / second) */
const SEPARATION_FORCE = 25;

/** Drop target if further than this (grid cells) ≈ 1000 game units */
const LEASH_RANGE_GRID = 1000 / CELL_SIZE;

/* ══════════════════ Upgrade & movement-speed helpers ══════════════════ */

/**
 * Minion upgrade count at a given game time.
 * Upgrades start at 0:30 and repeat every 90 s.
 */
function getUpgradeCount(gameTimeSec: number): number {
  if (gameTimeSec < 30) return 0;
  return Math.floor((gameTimeSec - 30) / 90) + 1;
}

/**
 * Minion base movement speed scales by game minute.
 * 350 / 375 / 400 / 425 / 450
 */
function getMinionMoveSpeed(gameTimeSec: number): number {
  const min = gameTimeSec / 60;
  if (min < 10) return 350;
  if (min < 20) return 375;
  if (min < 30) return 400;
  if (min < 40) return 425;
  return 450;
}

/* ══════════════════ Per-type stat computation ══════════════════ */

export function computeMinionStats(
  type: MinionType,
  upgrade: number,
  gameTimeSec: number,
): MinionStats {
  const ms = getMinionMoveSpeed(gameTimeSec);

  switch (type) {
    /* ── Melee ── */
    case 'melee': {
      const hpBonus = Math.min(
        25 * Math.min(upgrade, 5) + 35 * Math.max(0, upgrade - 5),
        1060,
      );
      const adBonus = Math.min(3 * Math.max(0, upgrade - 5), 69);
      const armor =
        upgrade <= 6
          ? 0
          : Math.min(0.085 * (upgrade - 5) * (upgrade - 6) / 2, 20);
      return {
        maxHealth: 465 + hpBonus,
        attackDamage: 11 + adBonus,
        attackSpeed: 1.25,
        attackRange: 110,
        moveSpeed: ms,
        armor,
        magicResist: 0,
      };
    }

    /* ── Caster ── */
    case 'caster': {
      const hpBonus = Math.min(9 * upgrade, 325);
      const adBonus = Math.min(
        1.5 * Math.min(upgrade, 5) + 4 * Math.max(0, upgrade - 5),
        105.5,
      );
      return {
        maxHealth: 275 + hpBonus,
        attackDamage: 19.5 + adBonus,
        attackSpeed: 0.667,
        attackRange: 550,
        moveSpeed: ms,
        armor: 0,
        magicResist: 0,
      };
    }

    /* ── Siege (cannon) ── */
    case 'siege': {
      const hpBonus = Math.min(85 * upgrade, 5100);
      const adBonus = Math.min(
        1.5 * Math.min(upgrade, 14) + 4 * Math.max(0, upgrade - 14),
        90,
      );
      return {
        maxHealth: 750 + hpBonus,
        attackDamage: 36 + adBonus,
        attackSpeed: 1.0,
        attackRange: 300,
        moveSpeed: ms,
        armor: 0,
        magicResist: 0,
      };
    }

    /* ── Super ── */
    case 'super':
    default:
      return {
        maxHealth: 1500,
        attackDamage: 190,
        attackSpeed: 0.694,
        attackRange: 170,
        moveSpeed: ms,
        armor: 100,
        magicResist: 100,
      };
  }
}

/* ══════════════════ Wave composition ══════════════════ */

export function getWaveInterval(gameTimeSec: number): number {
  if (gameTimeSec < 14 * 60) return 30;
  if (gameTimeSec < 30 * 60) return 25;
  return 20;
}

export function getWaveComposition(
  waveNumber: number,
  gameTimeSec: number,
): MinionType[] {
  const types: MinionType[] = [];

  let hasSiege = false;
  if (waveNumber >= 3) {
    if (gameTimeSec >= 25 * 60) {
      hasSiege = true;
    } else if (gameTimeSec >= 14 * 60) {
      hasSiege = waveNumber % 2 === 0;
    } else {
      hasSiege = waveNumber % 3 === 0;
    }
  }

  const meleeCount = hasSiege && gameTimeSec >= 14 * 60 ? 2 : 3;
  for (let i = 0; i < meleeCount; i++) types.push('melee');
  if (hasSiege) types.push('siege');
  const casterCount = gameTimeSec >= 30 * 60 ? 2 : 3;
  for (let i = 0; i < casterCount; i++) types.push('caster');

  return types;
}

/* ══════════════════ State creation ══════════════════ */

export function createInitialState(): SimulationState {
  return {
    gameTime: 0,
    minions: [],
    towers: createTowers(),
    jungleCamps: createJungleCamps(),
    spawnQueue: [],
    nextMinionId: 1,
    waveCounter: { blue: 0, red: 0 },
    nextWaveTime: { blue: FIRST_WAVE_TIME, red: FIRST_WAVE_TIME },
    running: false,
    speed: 1,
  };
}

/* ══════════════════ Spawning ══════════════════ */

function materializeMinion(
  state: SimulationState,
  q: QueuedMinion,
): Minion {
  const upgrade = getUpgradeCount(state.gameTime);
  const stats = computeMinionStats(q.type, upgrade, state.gameTime);

  const path = LANE_PATHS[q.lane];
  const isBlue = q.team === 'blue';
  const spawnWp = isBlue ? 0 : path.waypoints.length - 1;
  const nextWp = isBlue ? 1 : path.waypoints.length - 2;

  // Random perpendicular offset so minions spread across the lane
  const offsetRange = LANE_OFFSET_RANGE[q.type];
  const laneOffset = (Math.random() * 2 - 1) * offsetRange;

  const id = state.nextMinionId++;
  return {
    id,
    type: q.type,
    team: q.team,
    lane: q.lane,
    x: path.waypoints[spawnWp].x,
    y: path.waypoints[spawnWp].y,
    health: stats.maxHealth,
    maxHealth: stats.maxHealth,
    stats,
    waypointIdx: nextWp,
    targetId: null,
    towerTargetId: null,
    attackCooldown: 0,
    alive: true,
    waveNumber: q.waveNumber,
    laneOffset,
  };
}

function queueWaveForTeam(state: SimulationState, team: Team): void {
  state.waveCounter[team]++;
  const waveNum = state.waveCounter[team];
  const comp = getWaveComposition(waveNum, state.gameTime);
  const baseTime = state.gameTime;

  const lanes: Lane[] = ['top', 'mid', 'bot'];
  for (const lane of lanes) {
    for (let i = 0; i < comp.length; i++) {
      state.spawnQueue.push({
        spawnAt: baseTime + i * MINION_SPAWN_INTERVAL,
        type: comp[i],
        team,
        lane,
        waveNumber: waveNum,
      });
    }
  }
}

/* ══════════════════ Movement ══════════════════ */

/**
 * Walk a minion along its lane path with a perpendicular offset
 * so that minions naturally spread across the lane width.
 */
function moveMinion(m: Minion, dt: number): void {
  const path = LANE_PATHS[m.lane];
  if (m.waypointIdx < 0 || m.waypointIdx >= path.waypoints.length) return;

  const wp = path.waypoints[m.waypointIdx];
  const dir = m.team === 'blue' ? 1 : -1;
  const prevIdx = m.waypointIdx - dir;

  // Compute perpendicular offset from the lane centre-line
  let offX = 0;
  let offY = 0;
  if (
    prevIdx >= 0 &&
    prevIdx < path.waypoints.length &&
    m.laneOffset !== 0
  ) {
    const prev = path.waypoints[prevIdx];
    const sdx = wp.x - prev.x;
    const sdy = wp.y - prev.y;
    const slen = Math.sqrt(sdx * sdx + sdy * sdy);
    if (slen > 0.01) {
      // Perpendicular: rotate segment direction 90°
      offX = (-sdy / slen) * m.laneOffset;
      offY = (sdx / slen) * m.laneOffset;
    }
  }

  const goalX = wp.x + offX;
  const goalY = wp.y + offY;
  const dx = goalX - m.x;
  const dy = goalY - m.y;
  const dist = Math.sqrt(dx * dx + dy * dy);

  const moveGrid = (m.stats.moveSpeed / CELL_SIZE) * dt;

  if (dist <= moveGrid) {
    m.x = goalX;
    m.y = goalY;
    const next = m.waypointIdx + dir;
    if (next >= 0 && next < path.waypoints.length) {
      m.waypointIdx = next;
    }
  } else {
    const scale = moveGrid / dist;
    m.x += dx * scale;
    m.y += dy * scale;
  }
}

/* ══════════════════ Distance helpers ══════════════════ */

function distGrid(
  a: { x: number; y: number },
  b: { x: number; y: number },
): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function distToTower(m: Minion, t: Tower): number {
  return Math.sqrt((m.x - t.gx) ** 2 + (m.y - t.gy) ** 2);
}

/* ══════════════════ Target acquisition ══════════════════ */

/**
 * Find the best enemy minion to attack.
 * Spreads attacks: prefer targets that have fewer allied attackers.
 * Break ties by distance (closest first).
 */
function findMinionTarget(m: Minion, all: Minion[]): number | null {
  let bestId: number | null = null;
  let bestDist = Infinity;
  let bestAttackers = Infinity;

  for (const other of all) {
    if (!other.alive || other.team === m.team) continue;
    if (other.lane !== m.lane) continue;
    const d = distGrid(m, other);
    if (d >= ACQUISITION_RANGE_GRID) continue;

    // Count how many allied minions already target this enemy
    let attackers = 0;
    for (const ally of all) {
      if (
        ally.alive &&
        ally.team === m.team &&
        ally.id !== m.id &&
        ally.targetId === other.id
      ) {
        attackers++;
      }
    }

    // Prefer fewer attackers, then closer distance
    if (
      attackers < bestAttackers ||
      (attackers === bestAttackers && d < bestDist)
    ) {
      bestAttackers = attackers;
      bestDist = d;
      bestId = other.id;
    }
  }
  return bestId;
}

/**
 * Find the first enemy tower a minion should attack on its lane.
 * Towers must be attacked in tier order: outer → inner → inhib → nexus.
 */
function findTowerTarget(m: Minion, towers: Tower[]): string | null {
  const enemyTowers = towers.filter(
    (t) =>
      t.alive && t.team !== m.team && (t.lane === m.lane || t.lane === 'base'),
  );

  for (const tier of TIER_ORDER) {
    const tower = enemyTowers.find((t) => t.tier === tier);
    if (tower) {
      const d = distToTower(m, tower);
      if (d < ACQUISITION_RANGE_GRID) return tower.id;
      return null; // tier alive but out of range — don't skip
    }
  }
  return null;
}

/**
 * Tower target acquisition: closest enemy minion in range.
 * Priority: melee > siege > caster > super.
 */
function findTowerMinionTarget(
  tower: Tower,
  minions: Minion[],
): number | null {
  let bestId: number | null = null;
  let bestDist = Infinity;
  let bestPriority = Infinity;

  const priority: Record<string, number> = {
    melee: 0,
    siege: 1,
    caster: 2,
    super: 3,
  };

  for (const m of minions) {
    if (!m.alive || m.team === tower.team) continue;
    if (tower.lane !== 'base' && m.lane !== tower.lane) continue;
    const d = distToTower(m, tower);
    if (d > tower.stats.attackRange) continue;

    const p = priority[m.type] ?? 99;
    if (p < bestPriority || (p === bestPriority && d < bestDist)) {
      bestPriority = p;
      bestDist = d;
      bestId = m.id;
    }
  }
  return bestId;
}

/* ══════════════════ Combat ══════════════════ */

/** Bonus % current-HP damage minions deal to other minions. */
const MINION_VS_MINION_BONUS: Record<MinionType, number> = {
  melee: 0.02,
  caster: 0.04,
  siege: 0.06,
  super: 0,
};

/** Minions deal 60 % damage to structures. */
const MINION_VS_STRUCTURE_MOD = 0.6;

/** Tower damage to minions: % of target max-HP (pre-mitigation). */
const TOWER_VS_MINION_PCT: Record<TowerTier, Record<MinionType, number>> = {
  outer: { melee: 0.45, caster: 0.70, siege: 0.14, super: 0.05 },
  inner: { melee: 0.45, caster: 0.70, siege: 0.11, super: 0.05 },
  inhib: { melee: 0.45, caster: 0.70, siege: 0.08, super: 0.05 },
  nexus: { melee: 0.45, caster: 0.70, siege: 0.08, super: 0.05 },
};

/** Minion → minion (includes bonus % current-HP damage). */
function resolveMinionAttack(attacker: Minion, target: Minion): void {
  const baseDmg = attacker.stats.attackDamage;
  const bonusDmg = MINION_VS_MINION_BONUS[attacker.type] * target.health;
  const rawDmg = baseDmg + bonusDmg;
  const armorMod = 100 / (100 + target.stats.armor);
  target.health -= rawDmg * armorMod;
  if (target.health <= 0) target.alive = false;
}

/** Minion → tower (60 % modifier, reduced by tower armor). */
function resolveMinionAttackTower(attacker: Minion, tower: Tower): void {
  const rawDmg = attacker.stats.attackDamage * MINION_VS_STRUCTURE_MOD;
  const armorMod = 100 / (100 + tower.stats.armor);
  tower.health -= rawDmg * armorMod;
  if (tower.health <= 0) {
    tower.alive = false;
    tower.targetId = null;
  }
}

/** Tower → minion (% max-HP pre-mitigation, then armor). */
function resolveTowerAttack(tower: Tower, target: Minion): void {
  const pct = TOWER_VS_MINION_PCT[tower.tier]?.[target.type] ?? 0.45;
  const rawDmg = pct * target.maxHealth;
  const armorMod = 100 / (100 + target.stats.armor);
  target.health -= rawDmg * armorMod;
  if (target.health <= 0) target.alive = false;
}

/* ══════════════════ Separation steering ══════════════════ */

/**
 * Push apart minions that overlap on the same lane.
 * Applied after movement so it acts as a soft collision layer.
 */
function applySeparation(minions: Minion[], dt: number): void {
  for (let i = 0; i < minions.length; i++) {
    const a = minions[i];
    if (!a.alive) continue;
    for (let j = i + 1; j < minions.length; j++) {
      const b = minions[j];
      if (!b.alive || a.lane !== b.lane) continue;

      const dx = a.x - b.x;
      const dy = a.y - b.y;
      const d = Math.sqrt(dx * dx + dy * dy);
      if (d >= SEPARATION_RADIUS || d < 0.1) continue;

      const push =
        ((SEPARATION_RADIUS - d) / SEPARATION_RADIUS) *
        SEPARATION_FORCE *
        dt;
      const nx = dx / d;
      const ny = dy / d;
      a.x += nx * push * 0.5;
      a.y += ny * push * 0.5;
      b.x -= nx * push * 0.5;
      b.y -= ny * push * 0.5;
    }
  }
}

/* ══════════════════ Main tick (mutates in-place) ══════════════════ */

export function tickSimulation(state: SimulationState, dt: number): void {
  state.gameTime += dt;

  /* ── 0. Update movement speed for all living minions ── */
  const currentMs = getMinionMoveSpeed(state.gameTime);
  for (const m of state.minions) {
    if (m.alive) m.stats.moveSpeed = currentMs;
  }

  /* ── 1. Queue new waves ── */
  for (const team of ['blue', 'red'] as Team[]) {
    if (state.gameTime >= state.nextWaveTime[team]) {
      queueWaveForTeam(state, team);
      const interval = getWaveInterval(state.gameTime);
      state.nextWaveTime[team] += interval;
    }
  }

  /* ── 2. Materialize queued minions ── */
  const ready: QueuedMinion[] = [];
  const remaining: QueuedMinion[] = [];
  for (const q of state.spawnQueue) {
    if (state.gameTime >= q.spawnAt) ready.push(q);
    else remaining.push(q);
  }
  state.spawnQueue = remaining;
  for (const q of ready) {
    state.minions.push(materializeMinion(state, q));
  }

  /* ── 3. For each minion: validate targets → acquire → fight or walk ── */
  for (const m of state.minions) {
    if (!m.alive) continue;

    // --- Validate current minion target (alive & in leash range) ---
    if (m.targetId !== null) {
      const tgt = state.minions.find((o) => o.id === m.targetId);
      if (!tgt || !tgt.alive || distGrid(m, tgt) > LEASH_RANGE_GRID) {
        m.targetId = null;
      }
    }

    // --- Validate current tower target ---
    if (m.towerTargetId !== null) {
      const tt = state.towers.find((t) => t.id === m.towerTargetId);
      if (!tt || !tt.alive) m.towerTargetId = null;
    }

    // --- Acquire enemy-minion target (prioritise, spread attacks) ---
    if (m.targetId === null) {
      m.targetId = findMinionTarget(m, state.minions);
    }

    // --- If no minion target, try tower ---
    if (m.targetId === null && m.towerTargetId === null) {
      m.towerTargetId = findTowerTarget(m, state.towers);
    }

    // ── Fight enemy minion ──
    if (m.targetId !== null) {
      m.towerTargetId = null;
      const tgt = state.minions.find((o) => o.id === m.targetId)!;
      const d = distGrid(m, tgt);
      const rangeGrid = m.stats.attackRange / CELL_SIZE;

      if (d <= rangeGrid) {
        // In range — auto-attack
        m.attackCooldown -= dt;
        if (m.attackCooldown <= 0) {
          resolveMinionAttack(m, tgt);
          m.attackCooldown = 1 / m.stats.attackSpeed;
        }
      } else {
        // Move toward target
        const dx = tgt.x - m.x;
        const dy = tgt.y - m.y;
        const moveGrid = (m.stats.moveSpeed / CELL_SIZE) * dt;
        const scale = moveGrid / d;
        m.x += dx * scale;
        m.y += dy * scale;
      }
    }
    // ── Fight tower ──
    else if (m.towerTargetId) {
      const tower = state.towers.find((t) => t.id === m.towerTargetId)!;
      const d = distToTower(m, tower);
      const rangeGrid = m.stats.attackRange / CELL_SIZE;

      if (d <= rangeGrid) {
        m.attackCooldown -= dt;
        if (m.attackCooldown <= 0) {
          resolveMinionAttackTower(m, tower);
          m.attackCooldown = 1 / m.stats.attackSpeed;
        }
      } else {
        const dx = tower.gx - m.x;
        const dy = tower.gy - m.y;
        const moveGrid = (m.stats.moveSpeed / CELL_SIZE) * dt;
        const scale = moveGrid / d;
        m.x += dx * scale;
        m.y += dy * scale;
      }
    }
    // ── No target — walk lane (with offset) ──
    else {
      moveMinion(m, dt);
    }
  }

  /* ── 3b. Separation steering ── */
  applySeparation(state.minions, dt);

  /* ── 4. Towers attack enemy minions ── */
  for (const tower of state.towers) {
    if (!tower.alive) continue;

    if (tower.targetId !== null) {
      const tgt = state.minions.find((mi) => mi.id === tower.targetId);
      if (!tgt || !tgt.alive) tower.targetId = null;
    }

    if (tower.targetId === null) {
      tower.targetId = findTowerMinionTarget(tower, state.minions);
    }

    if (tower.targetId !== null) {
      tower.attackCooldown -= dt;
      if (tower.attackCooldown <= 0) {
        const tgt = state.minions.find((mi) => mi.id === tower.targetId);
        if (tgt && tgt.alive) {
          resolveTowerAttack(tower, tgt);
          tower.attackCooldown = 1 / tower.stats.attackSpeed;
        } else {
          tower.targetId = null;
        }
      }
    }
  }

  /* ── 5. Jungle camp spawn / respawn logic ── */
  tickJungleCamps(state);

  /* ── 6. Remove dead minions ── */
  state.minions = state.minions.filter((m) => m.alive);
}

/* ══════════════════ Jungle camp tick ══════════════════ */

function tickJungleCamps(state: SimulationState): void {
  const camps = state.jungleCamps;

  /* ── Baron-pit transition: despawn previous occupant 5 s before next ── */
  for (let i = 0; i < BARON_PIT_SEQUENCE.length - 1; i++) {
    const current = camps.find((c) => c.id === BARON_PIT_SEQUENCE[i]);
    const next = camps.find((c) => c.id === BARON_PIT_SEQUENCE[i + 1]);
    if (current && next && current.alive && state.gameTime >= next.initialSpawn - 5) {
      current.alive = false;
      current.killedAt = state.gameTime;
      current.nextSpawnAt = Infinity; // never respawns
    }
  }

  /* ── Spawn / respawn regular camps ── */
  for (const camp of camps) {
    if (!camp.alive && state.gameTime >= camp.nextSpawnAt && camp.nextSpawnAt < Infinity) {
      camp.alive = true;
    }
  }

  /* ── Scuttle special handling:
       When a scuttle is killed, the next spawn is on a random side after 2:30.
       After the initial 2:55 double-spawn, only one scuttle is ever alive. ── */
  // (Handled when killing — see killJungleCamp)
}

/* ══════════════════ Kill a jungle camp (called externally) ══════════════════ */

/**
 * Kill a jungle camp and handle respawn logic.
 * Returns true if the camp was alive and is now dead.
 */
export function killJungleCamp(state: SimulationState, campId: string): boolean {
  const camp = state.jungleCamps.find((c) => c.id === campId);
  if (!camp || !camp.alive) return false;

  camp.alive = false;
  camp.killedAt = state.gameTime;

  /* ── Scuttle crab ── */
  if (camp.type === 'scuttle') {
    const topScuttle = state.jungleCamps.find((c) => c.id === 'scuttle-top')!;
    const botScuttle = state.jungleCamps.find((c) => c.id === 'scuttle-bot')!;
    const respawnTime = camp.respawnTime; // 2:30

    // Pick a random side for the next scuttle
    const nextSide = Math.random() < 0.5 ? topScuttle : botScuttle;
    const otherSide = nextSide === topScuttle ? botScuttle : topScuttle;

    nextSide.nextSpawnAt = state.gameTime + respawnTime;
    otherSide.nextSpawnAt = Infinity; // only one scuttle after initial pair
  }
  /* ── Baron pit camps (void grubs, rift herald) — no respawn ── */
  else if (camp.respawnTime === 0) {
    camp.nextSpawnAt = Infinity;
  }
  /* ── Regular camps & baron — respawn after timer ── */
  else {
    camp.nextSpawnAt = state.gameTime + camp.respawnTime;
  }

  return true;
}
